import { and, asc, eq, inArray, isNull } from 'drizzle-orm'

import type { Database } from '../client.ts'
import { err, ok, type Result } from '../result.ts'
import { type Asset, asset, type assetEntityType, assetLink } from '../schema/assets.ts'
import { user } from '../schema/auth.ts'
import { type DbError, dbError } from './errors.ts'

type UserRow = typeof user.$inferSelect
type Tx = Parameters<Parameters<Database['transaction']>[0]>[0]

/** Re-exported so callers stay on the `queries/*` subpath and never reach into `schema/*`. */
export type { Asset }

/** The `asset_link.entity_type` values, as a type. */
export type AssetEntityType = (typeof assetEntityType.enumValues)[number]

/**
 * Which picture, on which row. `role` is what separates "the cover" from "one
 * of the gallery shots", so a query can ask for exactly one of them; `cover`
 * is the single-image case every catalogue entity uses today.
 */
export type AssetTarget = {
  entityType: AssetEntityType
  entityId: string
  role: string
}

/** The one-picture-per-row role: an equipment card, a studio header, an avatar. */
export const COVER_ROLE = 'cover'

/**
 * The `asset_link.role` an avatar occupies. One per user: the unique index is
 * `(assetId, entityType, entityId, role)`, so a SECOND avatar for the same user
 * would slip past it on a new `assetId` — the replace below deletes the old link
 * explicitly rather than relying on the constraint.
 */
export const AVATAR_ROLE = 'avatar'

/**
 * What an upload knows about itself once the bytes are in the store.
 *
 * The optional fields spell out `| undefined` because the repo runs
 * `exactOptionalPropertyTypes`: without it, "absent" and "present but
 * undefined" are different types and a caller spreading a partial object is
 * rejected.
 */
export type NewPublicAsset = {
  url: string
  pathname: string
  contentType: string
  sizeBytes: number
  originalFileName?: string | null | undefined
  altText?: string | null | undefined
  uploadedByUserId?: string | null | undefined
}

/** Rows whose blob is no longer referenced — the caller deletes the bytes. */
export type SupersededAssets = { superseded: Asset[] }

// ── generic entity images ───────────────────────────────────────────────────

/**
 * Point one entity's single-image slot at a freshly uploaded blob, replacing
 * whatever was there, in one transaction.
 *
 * Returns the assets that just lost their link. Their blobs are now unreachable
 * and the caller is expected to delete them from the store; the rows are only
 * SOFT-deleted so a URL still in someone's cache stays explainable rather than
 * becoming a mystery 404.
 *
 * `entityId` is NOT a foreign key (see the `asset_link` block comment) — this
 * cannot verify the row exists, so callers load the entity first. That check is
 * a `@repo/core` job anyway: it is the layer that knows a missing row is a
 * `not_found` rather than a persistence failure.
 */
export async function setEntityImage(
  db: Database,
  target: AssetTarget,
  input: NewPublicAsset,
): Promise<Result<{ asset: Asset; superseded: Asset[] }, DbError>> {
  try {
    const result = await db.transaction(async (tx) => {
      const created = await insertAsset(tx, input)
      const superseded = await detachLinks(tx, target)
      await linkAsset(tx, created.id, target)
      return { asset: created, superseded }
    })
    return ok(result)
  } catch (cause) {
    return err(dbError('Failed to set entity image', cause))
  }
}

/** Drop an entity's image. Idempotent — no image yields an empty list. */
export async function clearEntityImage(
  db: Database,
  target: AssetTarget,
): Promise<Result<SupersededAssets, DbError>> {
  try {
    const superseded = await db.transaction((tx) => detachLinks(tx, target))
    return ok({ superseded })
  } catch (cause) {
    return err(dbError('Failed to clear entity image', cause))
  }
}

/**
 * The live image for each of many rows, keyed by `entityId` — one query for a
 * whole listing rather than one per card.
 *
 * Soft-deleted assets are excluded: a row keeps its `asset` for the audit trail
 * after the blob is gone, and rendering that URL would give every card a broken
 * image. A `LEFT JOIN` would be wrong here for the same reason — the absence of
 * a usable picture must look identical to never having had one.
 */
export async function listEntityImages(
  db: Database,
  entityType: AssetEntityType,
  role: string,
  entityIds: readonly string[],
): Promise<Result<Map<string, Asset>, DbError>> {
  if (entityIds.length === 0) return ok(new Map())
  try {
    const rows = await db
      .select({ entityId: assetLink.entityId, asset })
      .from(assetLink)
      .innerJoin(asset, eq(asset.id, assetLink.assetId))
      .where(
        and(
          eq(assetLink.entityType, entityType),
          eq(assetLink.role, role),
          inArray(assetLink.entityId, [...entityIds]),
          isNull(asset.deletedAt),
        ),
      )
      .orderBy(asc(assetLink.sortOrder))

    const byEntity = new Map<string, Asset>()
    for (const row of rows) {
      // First wins: `sortOrder` decides when a role somehow holds more than one.
      if (!byEntity.has(row.entityId)) byEntity.set(row.entityId, row.asset)
    }
    return ok(byEntity)
  } catch (cause) {
    return err(dbError('Failed to list entity images', cause))
  }
}

/**
 * Unlink and soft-delete EVERY image on a row, whatever the role — what a
 * caller runs just before deleting the row itself. `asset_link.entity_id` is not
 * a foreign key, so nothing cascades: skipping this would leave links pointing
 * at an id that no longer exists, and the blobs unreachable but still paid for.
 */
export async function clearAllEntityImages(
  db: Database,
  entityType: AssetEntityType,
  entityId: string,
): Promise<Result<SupersededAssets, DbError>> {
  try {
    const superseded = await db.transaction(async (tx) => {
      const links = await tx
        .select({ assetId: assetLink.assetId })
        .from(assetLink)
        .where(and(eq(assetLink.entityType, entityType), eq(assetLink.entityId, entityId)))
      const ids = links.map((link) => link.assetId)
      if (ids.length === 0) return []

      await tx
        .delete(assetLink)
        .where(and(eq(assetLink.entityType, entityType), eq(assetLink.entityId, entityId)))
      return await softDelete(tx, ids)
    })
    return ok({ superseded })
  } catch (cause) {
    return err(dbError('Failed to clear entity images', cause))
  }
}

// ── avatars (an entity image, plus the denormalised column) ─────────────────

/**
 * Point a user's avatar at a freshly uploaded blob, in one transaction:
 * index the asset, repoint the link, and denormalise the URL onto `user.image`.
 *
 * `user.image` is a copy, not a join, and deliberately so: better-auth reads
 * that column when it builds a session, so every header render would otherwise
 * pay for an `asset_link` lookup. The `asset` row is still what makes the blob
 * explainable (who uploaded it, when, how big) and what a future admin surface
 * lists — the column alone cannot answer any of that.
 *
 * That extra column is the whole reason this is not just `setEntityImage`.
 */
export async function setUserAvatar(
  db: Database,
  userId: string,
  input: NewPublicAsset,
): Promise<Result<{ user: UserRow; superseded: Asset[] }, DbError>> {
  const target: AssetTarget = { entityType: 'USER', entityId: userId, role: AVATAR_ROLE }
  try {
    const result = await db.transaction(async (tx) => {
      // The uploader defaults to the owner: an avatar is always self-served.
      const created = await insertAsset(tx, {
        ...input,
        uploadedByUserId: input.uploadedByUserId ?? userId,
      })
      const superseded = await detachLinks(tx, target)
      await linkAsset(tx, created.id, target)

      const [updated] = await tx
        .update(user)
        .set({ image: created.url, updatedAt: new Date() })
        .where(eq(user.id, userId))
        .returning()
      if (!updated) throw new Error(`user ${userId} not found`)

      return { user: updated, superseded }
    })
    return ok(result)
  } catch (cause) {
    return err(dbError('Failed to set user avatar', cause))
  }
}

/**
 * Drop the avatar: unlink, soft-delete, clear `user.image`. Idempotent — a user
 * with no avatar yields an empty `superseded` list and an unchanged row.
 */
export async function clearUserAvatar(
  db: Database,
  userId: string,
): Promise<Result<SupersededAssets, DbError>> {
  try {
    const superseded = await db.transaction(async (tx) => {
      const dropped = await detachLinks(tx, {
        entityType: 'USER',
        entityId: userId,
        role: AVATAR_ROLE,
      })
      await tx.update(user).set({ image: null, updatedAt: new Date() }).where(eq(user.id, userId))
      return dropped
    })
    return ok({ superseded })
  } catch (cause) {
    return err(dbError('Failed to clear user avatar', cause))
  }
}

// ── shared transaction steps ────────────────────────────────────────────────
//
// Typed against the transaction handle rather than `Database` because Drizzle's
// `tx` is a distinct type; taking the union would let a caller pass the pool and
// lose atomicity without the compiler noticing. These throw instead of returning
// a Result — inside a transaction a throw is what rolls it back, and each public
// function above catches at its own boundary.

/**
 * Index a blob that lives in the PRIVATE store.
 *
 * Its own function rather than a `store` argument on the public one, because
 * the two stores are a security boundary and an argument is a thing you can
 * forget: `insertAsset(row)` defaulting to PUBLIC would index a deliverable as
 * world-readable and nothing would look wrong at the call site.
 *
 * No `altText` — that field exists for the marketing surface, where a11y is not
 * optional. A private deliverable is not decoration on a page.
 */
export async function insertPrivateAsset(
  db: Database,
  input: Omit<NewPublicAsset, 'altText'>,
): Promise<Result<Asset, DbError>> {
  try {
    const [created] = await db
      .insert(asset)
      .values({
        store: 'PRIVATE',
        url: input.url,
        pathname: input.pathname,
        contentType: input.contentType,
        sizeBytes: input.sizeBytes,
        originalFileName: input.originalFileName ?? null,
        uploadedByUserId: input.uploadedByUserId ?? null,
      })
      .returning()
    if (!created) return err(dbError('Private asset insert returned no row'))
    return ok(created)
  } catch (cause) {
    return err(dbError('Failed to index a private asset', cause))
  }
}

async function insertAsset(tx: Tx, input: NewPublicAsset): Promise<Asset> {
  const [created] = await tx
    .insert(asset)
    .values({
      store: 'PUBLIC',
      url: input.url,
      pathname: input.pathname,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalFileName: input.originalFileName ?? null,
      altText: input.altText ?? null,
      uploadedByUserId: input.uploadedByUserId ?? null,
    })
    .returning()
  if (!created) throw new Error('asset insert returned no row')
  return created
}

async function linkAsset(tx: Tx, assetId: string, target: AssetTarget): Promise<void> {
  await tx.insert(assetLink).values({
    assetId,
    entityType: target.entityType,
    entityId: target.entityId,
    role: target.role,
  })
}

/** Remove every link on one (entity, role) and soft-delete the assets behind them. */
async function detachLinks(tx: Tx, target: AssetTarget): Promise<Asset[]> {
  const where = and(
    eq(assetLink.entityType, target.entityType),
    eq(assetLink.entityId, target.entityId),
    eq(assetLink.role, target.role),
  )

  const links = await tx.select({ assetId: assetLink.assetId }).from(assetLink).where(where)
  const ids = links.map((link) => link.assetId)
  if (ids.length === 0) return []

  await tx.delete(assetLink).where(where)
  return await softDelete(tx, ids)
}

async function softDelete(tx: Tx, ids: string[]): Promise<Asset[]> {
  return await tx
    .update(asset)
    .set({ deletedAt: new Date(), updatedAt: new Date() })
    .where(inArray(asset.id, ids))
    .returning()
}
